import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import BookingChat from '../components/BookingChat';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ChatCircleText, CircleNotch, MagnifyingGlass, ArrowRight, Eye, Buildings } from '@phosphor-icons/react';

const statusLabel = (s) => ({
  pending: 'In attesa', pending_pro: 'In attesa', notified: 'Inviata', confirmed: 'Confermata',
  accepted: 'Accettata', in_progress: 'In corso', completed: 'Completata', reviewed: 'Completata',
  cancelled: 'Annullata', payment_required: 'Pagamento richiesto',
}[s] || s);

const AdminMessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  const fetchConvs = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        api.get('/api/admin/conversations').catch(() => ({ data: {} })),
        api.get('/api/admin/request-conversations').catch(() => ({ data: {} })),
      ]);
      const bookings = (b.data?.conversations || []).map((c) => ({ ...c, kind: 'booking', key: c.booking_id }));
      const requests = (r.data?.conversations || []).map((c) => ({ ...c, kind: 'request', key: c.request_id }));
      const all = [...requests, ...bookings];
      all.sort((x, y) => (y.last_message_at || '').localeCompare(x.last_message_at || ''));
      setConversations(all);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConvs(); }, [fetchConvs]);

  const filtered = conversations.filter((c) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (c.client_name || '').toLowerCase().includes(t)
      || (c.professional_name || '').toLowerCase().includes(t)
      || (c.center_name || '').toLowerCase().includes(t)
      || (c.service_type || '').toLowerCase().includes(t);
  });

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="admin-messages-page">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-foreground">Comunicazioni</h2>
        <p className="text-sm text-slate-500 mt-1">Supervisione di tutte le conversazioni tra clienti, professionisti e Centri, note interne comprese (sola lettura).</p>
      </div>

      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca cliente, professionista, Centro o prestazione…"
          className="pl-9 rounded-full"
          data-testid="admin-messages-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <CircleNotch size={18} className="animate-spin" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center" data-testid="admin-messages-empty">
          <ChatCircleText size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <p className="text-foreground font-medium">Nessuna conversazione</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="admin-conversations-list">
          {filtered.map((c) => (
            <button
              key={c.key}
              onClick={() => setOpen(c)}
              data-testid={`admin-conversation-${c.key}`}
              className="w-full text-left bg-white rounded-2xl border border-[#E3F2FD] hover:border-primary/40 hover:shadow-[0_6px_20px_rgba(21,101,192,0.08)] transition-all p-4 flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-secondary text-primary flex items-center justify-center shrink-0">
                <ChatCircleText size={22} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{c.client_name}</span>
                  <ArrowRight size={14} className="text-slate-400" />
                  <span className="font-medium text-foreground">{c.professional_name}</span>
                  <Badge variant="outline" className="text-[10px] border-[#E3F2FD] text-slate-500">{statusLabel(c.status)}</Badge>
                  {c.kind === 'request' && <Badge className="text-[10px] bg-emerald-100 text-emerald-800">Richiesta</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                  {c.center_name && (<><Buildings size={12} /> {c.center_name} ·</>)}
                  {c.service_type} · {c.message_count} messaggi
                </div>
                <div className="text-sm text-slate-400 truncate mt-0.5">{c.last_message || '—'}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {c.last_message_at && (
                  <span className="text-[10px] text-slate-400">
                    {new Date(c.last_message_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                  </span>
                )}
                <span className="flex items-center gap-1 text-xs text-primary"><Eye size={14} /> Leggi</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <BookingChat
          bookingId={open.kind === 'booking' ? open.booking_id : undefined}
          requestId={open.kind === 'request' ? open.request_id : undefined}
          onClose={() => setOpen(null)}
          forceReadonly
        />
      )}
    </div>
  );
};

export default AdminMessagesPage;
